import { useState, useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { BidPriority, DEADLINE_TIME_NEEDS_DATE, type Bid, type BidRoute } from "@/lib/schemas"
import { deadlineFromInputValues, deadlineInputValues, inputTimeZoneName } from "@/lib/bid-deadline"
import { useBidsStore } from "@/stores"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { TagInput } from "@/components/ui/tag-input"
import { UserPicker } from "@/components/ui/user-picker"
import { SquarePen, Loader2, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"

// Schema for edit form (all fields optional except what we require)
const EditBidFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  client: z.string().min(1, "Client is required"),
  priority: BidPriority,
  value: z.union([
    z.number().positive("Value must be positive"),
    z.nan().transform(() => undefined),
  ]).optional().nullable(),
  deadline: z.string().optional(),
  deadlineTime: z.string().optional(),
  description: z.string().optional(),
}).refine((form) => !form.deadlineTime || !!form.deadline, {
  message: DEADLINE_TIME_NEEDS_DATE,
  path: ["deadline"],
})
type EditBidForm = z.infer<typeof EditBidFormSchema>

// The procurement route is a user-stated fact, not a prediction. It is NOT part
// of the form schema: the generic bid update strips scope fields, so it is held
// in local state and written through `setBidScope` (the `_scope` path).
const ROUTE_OPTIONS: { value: BidRoute; label: string }[] = [
  { value: "open_market", label: "Open market" },
  { value: "invited", label: "Invited" },
  { value: "panel_calloff", label: "Panel call-off" },
  { value: "subcontract_package", label: "Subcontract package" },
]

interface EditBidDialogProps {
  bid: Bid
  trigger?: React.ReactNode
  onSuccess?: () => void
  // Controlled mode props
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function EditBidDialog({ bid, trigger, onSuccess, open: controlledOpen, onOpenChange }: EditBidDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [tags, setTags] = useState<string[]>(bid.tags ?? [])
  const [assignedTo, setAssignedTo] = useState<string[]>(bid.assignedTo ?? [])
  // Nothing parses BidSchema at runtime, so a stale response can omit bidRoute
  // despite the type saying otherwise — fall back to the schema default.
  const [route, setRoute] = useState<BidRoute>(bid.bidRoute ?? "open_market")

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen
  const { updateBid, setBidScope, isLoading, users, fetchUsers, bids } = useBidsStore()

  // Fetch users on mount
  useEffect(() => {
    void fetchUsers()
  }, [fetchUsers])

  // Compute tag suggestions from existing bids
  const tagSuggestions = useMemo(() => {
    const allTags = bids.flatMap(b => b.tags ?? [])
    return [...new Set(allTags)]
  }, [bids])

  // The date and time inputs' values: the viewer's own date and time for a
  // deadline with a time, the stored date for a date-only one.
  const deadlineInputs = deadlineInputValues(bid)

  // React Hook Form with Zod resolver
  const form = useForm<EditBidForm>({
    resolver: zodResolver(EditBidFormSchema),
    defaultValues: {
      title: bid.title,
      client: bid.client,
      priority: bid.priority,
      value: bid.value ?? null,
      deadline: deadlineInputs.date,
      deadlineTime: deadlineInputs.time,
      description: bid.description ?? "",
    },
  })

  const {
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = form

  const selectedPriority = watch("priority")
  const deadlineDate = watch("deadline")

  // Reset form when bid changes or dialog opens
  useEffect(() => {
    if (open) {
      const inputs = deadlineInputValues(bid)
      reset({
        title: bid.title,
        client: bid.client,
          priority: bid.priority,
        value: bid.value ?? null,
        deadline: inputs.date,
        deadlineTime: inputs.time,
        description: bid.description ?? "",
      })
      setTags(bid.tags ?? [])
      setAssignedTo(bid.assignedTo ?? [])
      setRoute(bid.bidRoute ?? "open_market")
    }
  }, [open, bid, reset])

  const onSubmit = async (data: EditBidForm) => {
    setSubmitError(null)
    try {
      // A date alone is saved as before (UTC midnight of the date, date-only);
      // a date and time is that moment in this person's time zone. An emptied
      // field on a bid that had a deadline clears it (null); leaving it out
      // would keep the old date. A bid that never had one sends nothing, so
      // saving other edits never records a "cleared" deadline and extraction
      // can still fill it.
      const entered = deadlineFromInputValues(data.deadline, data.deadlineTime)
      const deadlineUpdate = entered
        ? { deadline: entered.deadline, deadlineHasTime: entered.deadlineHasTime }
        : bid.deadline
          ? { deadline: null }
          : {}

      await updateBid({
        id: bid.id,
        title: data.title,
        client: data.client,
        priority: data.priority,
        value: data.value ?? undefined,
        ...deadlineUpdate,
        description: data.description || undefined,
        tags,
        assignedTo,
      })

      // The route rides the `_scope` path — folding it into updateBid above
      // would be a silent no-op (the backend strips scope fields there).
      if (route !== (bid.bidRoute ?? "open_market")) {
        await setBidScope(bid.id, { bidRoute: route })
      }

      setOpen(false)
      onSuccess?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update bid"
      setSubmitError(message)
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSubmitError(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button variant="outline" size="sm" className="gap-2">
              <SquarePen className="h-4 w-4" />
              Edit
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
          <DialogHeader>
            <DialogTitle>Edit Bid</DialogTitle>
            <DialogDescription>
              Update the bid details below.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Title */}
            <div className="grid gap-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="e.g., Enterprise Software Development"
                {...register("title")}
                className={cn(errors.title && "border-destructive")}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            {/* Client */}
            <div className="grid gap-2">
              <Label htmlFor="client">
                Client <span className="text-destructive">*</span>
              </Label>
              <Input
                id="client"
                placeholder="e.g., Tech Corp Inc."
                {...register("client")}
                className={cn(errors.client && "border-destructive")}
              />
              {errors.client && (
                <p className="text-sm text-destructive">{errors.client.message}</p>
              )}
            </div>

            {/* Priority. The status select is gone (R4): where the bid is
                changes through the stage control, which also captures the
                outcome when a bid closes. */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="priority">
                  Priority <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={selectedPriority}
                  onValueChange={(value) => setValue("priority", value as typeof selectedPriority)}
                >
                  <SelectTrigger className={cn(errors.priority && "border-destructive")}>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-blue-500" />
                        Low
                      </span>
                    </SelectItem>
                    <SelectItem value="medium">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-amber-500" />
                        Medium
                      </span>
                    </SelectItem>
                    <SelectItem value="high">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-orange-500" />
                        High
                      </span>
                    </SelectItem>
                    <SelectItem value="critical">
                      <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-red-500" />
                        Critical
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {errors.priority && (
                  <p className="text-sm text-destructive">{errors.priority.message}</p>
                )}
              </div>
            </div>

            {/* Procurement route */}
            <div className="grid gap-2">
              <Label htmlFor="route">Procurement route</Label>
              <Select value={route} onValueChange={(value) => setRoute(value as BidRoute)}>
                <SelectTrigger id="route" aria-label="Procurement route">
                  <SelectValue placeholder="Select route" />
                </SelectTrigger>
                <SelectContent>
                  {ROUTE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value and Deadline row. The deadline column sizes to its date and
                time inputs; value takes the rest. Stacked below sm. Top-aligned:
                the Deadline column is taller (it carries the time-zone hint), and
                a stretched Value column spread its label and input apart, so the
                Value input sat below the date and time inputs. */}
            <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
              <div className="grid gap-2">
                <Label htmlFor="value">Value ($)</Label>
                <Input
                  id="value"
                  type="number"
                  placeholder="e.g., 100000"
                  {...register("value", { valueAsNumber: true })}
                  className={cn(errors.value && "border-destructive")}
                />
                {errors.value && (
                  <p className="text-sm text-destructive">{errors.value.message}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="deadline">Deadline</Label>
                <div className="flex gap-2">
                  <Input
                    id="deadline"
                    type="date"
                    {...register("deadline")}
                    className={cn("min-w-0 flex-1 sm:w-40 sm:flex-none", errors.deadline && "border-destructive")}
                  />
                  <Input
                    id="deadlineTime"
                    type="time"
                    aria-label="Closing time (optional)"
                    aria-describedby="deadlineTime-hint"
                    {...register("deadlineTime")}
                    className="w-32 shrink-0"
                  />
                </div>
                <p id="deadlineTime-hint" className="text-meta">
                  Time is optional, in your time zone ({inputTimeZoneName(deadlineDate)})
                </p>
                {errors.deadline && (
                  <p className="text-sm text-destructive">{errors.deadline.message}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of the bid opportunity..."
                rows={3}
                {...register("description")}
                className={cn(errors.description && "border-destructive")}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description.message}</p>
              )}
            </div>

            {/* Tags */}
            <div className="grid gap-2">
              <Label>Tags</Label>
              <TagInput value={tags} onChange={setTags} suggestions={tagSuggestions} placeholder="Add tags..." />
            </div>

            {/* Assigned Users */}
            <div className="grid gap-2">
              <Label>Assigned to</Label>
              <UserPicker value={assignedTo} onChange={setAssignedTo} users={users} />
            </div>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting || isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {(isSubmitting || isLoading) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
